/**
 * captureEmptyState — the copy an empty Capture list shows, per category.
 *
 * WHY THIS FILE EXISTS (Osama, QA-100, 2026-08-04)
 * "Empty-state messages are confusing when there are no captures ('Try a
 * different Search or Filter' / 'Clear Search and Filters'). Per-category empty
 * states: Photo 'You haven't captured a photo yet', Video/Audio/Document
 * likewise — and add the purple Capture button directly in the empty state."
 *
 * THE DISTINCTION THAT WAS MISSING
 * "nothing matched your search" and "you have never captured one of these" are
 * two different facts, and the screen used to print the first for both. Search
 * copy is only honest when a SEARCH is actually narrowing the list; a bare type
 * chip with zero items is not a failed search, it is an empty category — and an
 * empty category's useful next step is to create one, which is why the CTA
 * belongs inline.
 */

import { CAPTURE_TYPES, captureTypeLabel, FILTER_ALL } from "./Capture.helpers";

/** Lower-case noun for the "you haven't captured a ___ yet" sentence. */
const NOUN_BY_TYPE = {
  [CAPTURE_TYPES.TEXT]: "note",
  [CAPTURE_TYPES.PHOTO]: "photo",
  [CAPTURE_TYPES.VIDEO]: "video",
  [CAPTURE_TYPES.AUDIO]: "audio clip",
  [CAPTURE_TYPES.DOC]: "document",
  [CAPTURE_TYPES.LINK]: "link",
};

/** "a" / "an" for the noun that follows it. */
const articleFor = (noun) => (/^[aeiou]/i.test(String(noun)) ? "an" : "a");

/**
 * Describe the empty state the list should render.
 *
 * @param {object} args
 * @param {string} args.type   the active filter chip (`FILTER_ALL` or a CAPTURE_TYPES value)
 * @param {string} args.query  the active search string
 * @returns {{kind: 'search'|'category'|'all', title: string, body: string, showCta: boolean, ctaLabel: string}}
 *   `kind` is exposed so a caller can pick the right icon (a magnifier for a
 *   failed search, the type's own art for an empty category) without re-deriving
 *   the decision from the same inputs and drifting.
 */
export const captureEmptyState = ({ type = FILTER_ALL, query = "" } = {}) => {
  const searching = !!String(query || "").trim();

  // A search is narrowing the list — the honest message is about the SEARCH,
  // and offering "New capture" here would answer a question nobody asked.
  if (searching) {
    return {
      kind: "search",
      title: "No matching captures",
      body: "Try a different search or filter.",
      showCta: false,
      ctaLabel: "New capture",
    };
  }

  if (type === FILTER_ALL) {
    return {
      kind: "all",
      title: "Your capture inbox is empty",
      body: "Snap it now, place it later — capture notes, photos, videos, audio and documents, then attach them to a Wheel when you're ready.",
      showCta: true,
      ctaLabel: "New capture",
    };
  }

  const noun = NOUN_BY_TYPE[type] || String(captureTypeLabel(type)).toLowerCase();
  return {
    kind: "category",
    title: `You haven't captured ${articleFor(noun)} ${noun} yet.`,
    body: `Anything you capture as ${articleFor(noun)} ${noun} lands here.`,
    showCta: true,
    ctaLabel: "New capture",
  };
};
